import React, { useState, useEffect } from 'react';
import {
  collection, onSnapshot, updateDoc, setDoc,
  doc, query, orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { K, APPARATUS_DEFAULT, inputStyle } from '../constants';
import { NI, Lbl, SecHd, Metric } from '../components/ui';

function ApparatusCard({ u, isAdmin, flowGPM, onSave }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ unit: u.unit, name: u.name, pump: u.pump, tank: u.tank });

  const dur = flowGPM > 0 ? u.tank / flowGPM : 0;
  const durCol = dur < 2 ? K.bad : dur < 5 ? K.warn : K.ok;

  const save = () => {
    onSave(u.id, form);
    setEditing(false);
  };

  return (
    <div style={{ background: K.bg1, border: `1px solid ${editing ? K.red : K.bdr}`, borderLeft: `3px solid ${K.red}`, borderRadius: 12, padding: 14, boxShadow: K.sh1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontFamily: K.fMono, fontSize: 34, fontWeight: 500, color: K.red }}>{u.unit}</div>
        {isAdmin && (
          editing
            ? <button onClick={save} style={{ background: K.red, border: `1px solid ${K.red2}`, color: K.t0, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, fontWeight: 600 }}>Save</button>
            : <button onClick={() => setEditing(true)} style={{ background: K.bg3, border: `1px solid ${K.bdr2}`, color: K.t1, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, fontWeight: 600 }}>Edit</button>
        )}
      </div>

      {editing
        ? <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} style={{ ...inputStyle(), marginBottom: 10 }} />
        : <div style={{ fontSize: 11, color: K.t2, marginBottom: 12 }}>{u.name}</div>
      }

      {editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><Lbl t="Pump GPM" /><NI v={form.pump} set={(v) => setForm((f) => ({ ...f, pump: v }))} min={500} max={3000} /></div>
          <div><Lbl t="Tank gal" /><NI v={form.tank} set={(v) => setForm((f) => ({ ...f, tank: v }))} min={0} max={10000} /></div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <Metric label="Pump"       value={`${u.pump} GPM`}            col={K.t1}   sm />
          <Metric label="Tank"       value={`${u.tank} gal`}            col={K.t1}   sm />
          <Metric label={`@${flowGPM} GPM`} value={`${dur.toFixed(1)} min`} col={durCol} sm />
        </div>
      )}
    </div>
  );
}

export function Apparatus({ isAdmin }) {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading]     = useState(true);
  const [flowGPM, setFlowGPM]     = useState(150);

  // Load any admin overrides from Firestore (keyed by unit name)
  useEffect(() => {
    const q = query(collection(db, 'apparatus'), orderBy('order', 'asc'));
    return onSnapshot(q, (snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.data().unit] = { id: d.id, ...d.data() }; });
      setOverrides(map);
      setLoading(false);
    });
  }, []);

  // Merge defaults with any Firestore overrides
  const units = APPARATUS_DEFAULT.map((def, i) => {
    const over = overrides[def.unit];
    return {
      ...def,
      ...over,
      order: over?.order ?? i,
      _key: def.unit,
    };
  });

  const handleSave = async (u, fields) => {
    const data = { unit: fields.unit.trim(), name: fields.name.trim(), pump: fields.pump, tank: fields.tank, order: 0 };
    const existing = overrides[u.unit];
    if (existing) {
      await updateDoc(doc(db, 'apparatus', existing.id), data);
    } else {
      // Create override doc keyed by unit name for stable merging
      await setDoc(doc(db, 'apparatus', u.unit), data);
    }
  };

  return (
    <div>
      <SecHd title="Apparatus" sub="Pump specs and tank capacity — Central Mason Fire & EMS fleet." />
      <div style={{ marginBottom: 16 }}>
        <Lbl t="Reference Flow Rate (GPM)" />
        <NI v={flowGPM} set={setFlowGPM} min={50} max={2000} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: K.t3, fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          {units.filter((u) => u.pump && u.tank).map((u) => (
            <ApparatusCard
              key={u._key}
              u={u}
              isAdmin={isAdmin}
              flowGPM={flowGPM}
              onSave={(_, fields) => handleSave(u, fields)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
